import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type { ListDealsQuery, ListPaginatedDealsResult } from '../../../apis/dealsAPI'

export const DEALS_QUERY_KEY_ROOT = ['deals'] as const

export function dealsQueryKey(query: ListDealsQuery): readonly unknown[] {
  return [...DEALS_QUERY_KEY_ROOT, 'list', query]
}

export function useDealsQuery(query: ListDealsQuery): UseQueryResult<ListPaginatedDealsResult> {
  return useQuery<ListPaginatedDealsResult>(dealsQueryKey(query), () => dealsAPI.listDeals(query), {
    keepPreviousData: true,
  })
}

/**
 * Companion query used by filter dropdowns to compute counts across every
 * status bucket. Backend defaults (`status` omitted or `status=all`) exclude
 * canceled and archived deals, so we issue three parallel buckets and union
 * them so client-side `dealMatchesStatus` / `dealMatchesPayment` predicates
 * have visibility into every state. A dedicated counts endpoint would let us
 * collapse this back to a single request — left as a backend follow-up.
 */
// TODO: replace with /deals/counts backend endpoint once available; current 500-cap will silently undercount past that point.
export function useAllDealsForCountsQuery(): UseQueryResult<ListPaginatedDealsResult> {
  return useQuery<ListPaginatedDealsResult>(
    [...DEALS_QUERY_KEY_ROOT, 'counts'],
    async () => {
      const [active, canceled, archived] = await Promise.all([
        dealsAPI.listDeals({ page: 1, pagePerRecordCount: 500 }),
        dealsAPI.listDeals({ page: 1, pagePerRecordCount: 500, status: 'canceled' }),
        dealsAPI.listDeals({ page: 1, pagePerRecordCount: 500, status: 'archived' }),
      ])
      const buckets = [
        { name: 'active', bucket: active },
        { name: 'canceled', bucket: canceled },
        { name: 'archived', bucket: archived },
      ]
      for (const { name, bucket } of buckets) {
        if (bucket.total > bucket.deals.length) {
          // eslint-disable-next-line no-console
          console.warn(
            `[useAllDealsForCountsQuery] ${name} bucket truncated: ${bucket.deals.length}/${bucket.total}. Counts may be inaccurate. TODO: switch to /deals/counts endpoint when available.`,
          )
        }
      }
      const seen = new Set<string>()
      const merged = [...active.deals, ...canceled.deals, ...archived.deals].filter((deal) => {
        if (seen.has(deal.id)) return false
        seen.add(deal.id)
        return true
      })
      return {
        deals: merged,
        total: merged.length,
        currentPage: 1,
        pagePerRecordCount: merged.length,
      }
    },
    { keepPreviousData: true },
  )
}
